import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { blip } from "@/lib/audio";
import { toast } from "sonner";
import { Siren, Navigation, ShieldCheck, MapPin, Loader2, ShieldAlert } from "lucide-react";

// Shift Sign-On + Location Verification gate shown before the MDT loads.
export default function ShiftSignOn({ onComplete }) {
  const { user } = useAuth();
  const [step, setStep] = useState("signon");
  const [unit, setUnit] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [busy, setBusy] = useState(false);
  const now = new Date();

  useEffect(() => {
    // MDT sign-on is locked to the officer's own assigned unit (their call-sign badge).
    if (user?.badge) setUnit(user.badge);
  }, [user?.badge]);

  const signOn = () => {
    if (!unit.trim()) { toast.error("Unit / call sign required"); return; }
    blip();
    setStep("location");
  };

  const finish = (loc) => {
    localStorage.setItem("mdt_unit", unit);
    localStorage.setItem("mdt_shift", JSON.stringify({ unit, vehicle, signon: now.toISOString(), location: loc }));
    // Always drop the unit's dot EXACTLY where the officer logs on — best-effort
    // background GPS capture even when they chose Manual/Skip.
    if (loc !== "gps" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { client.post(`/units/${unit}/locate`, { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {}); },
        () => {}, { enableHighAccuracy: true, timeout: 8000 });
    }
    onComplete(unit);
  };

  const enableGps = () => {
    setBusy(true);
    if (!navigator.geolocation) { toast.error("Geolocation unavailable — enter manually"); setBusy(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try { await client.post(`/units/${unit}/locate`, { lat: pos.coords.latitude, lng: pos.coords.longitude }); } catch { /* unit may not exist */ }
        toast.success("GPS confirmed — location shared with dispatch");
        finish("gps");
      },
      () => { toast.error("GPS denied — enter manually or skip"); setBusy(false); },
      { timeout: 8000 });
  };

  return (
    <div className="app-viewport w-screen flex items-center justify-center bg-[#0b0d12] text-[#F3F4F6] grid-lines p-4 sm:p-6 overflow-y-auto">
      {user?.env === "training" && (
        <div data-testid="mdt-training-banner" className="fixed top-0 left-0 right-0 z-50 bg-[#F59E0B] text-black font-head font-bold text-[11px] text-center py-1 tracking-wide">
          TRAINING ENVIRONMENT — SIMULATION ONLY · NOT A LIVE SYSTEM
        </div>
      )}
      {step === "signon" && (
        <div className="w-full max-w-md" data-testid="shift-signon">
          <div className="text-center mb-6">
            <Siren size={28} className="text-[#3B82F6] mx-auto mb-2" />
            <div className="font-head font-bold text-2xl tracking-tight">SHIFT SIGN-ON</div>
            <div className="font-mono text-xs text-[#6b8bd6] mt-1">{user?.name}{user?.badge ? ` · Badge #${user.badge}` : ""}</div>
          </div>
          <div className="border border-[#2C2D35] bg-[#111621] p-5">
            <div data-testid="mdt-federal-warning" className="border border-[#DC2626]/60 bg-[#1a0b0c] p-3 mb-4 flex gap-2">
              <ShieldAlert size={26} className="text-[#DC2626] shrink-0 mt-0.5" />
              <div className="font-mono text-xs text-[#FCA5A5] leading-relaxed">
                <b className="text-[#EF4444] text-xs">WARNING — RESTRICTED LAW ENFORCEMENT SYSTEM.</b> This is a secured criminal
                justice information system. Unauthorized access, use, or attempted access is strictly prohibited and is
                continuously monitored, recorded, and logged. Violators are subject to criminal and civil penalties under
                <b> 18 U.S.C. § 1030 (Computer Fraud and Abuse Act)</b>, the <b>FBI CJIS Security Policy</b>, and
                <b> COMAR 12.15.01 (Maryland Criminal Justice Information System)</b>. By continuing you consent to
                monitoring and certify that you are an authorized user.
              </div>
            </div>
            <div className="border border-[#165c3a] bg-[#0e2a1c] text-[#4ade80] font-mono text-[11px] text-center px-3 py-2 mb-4">
              ✓ AUTHENTICATION VERIFIED — SIGN ON TO BEGIN SHIFT
            </div>
            <label className="font-mono text-[11px] text-[#6b8bd6] uppercase"># Unit / Call Sign</label>
            <div data-testid="signon-unit" className="mt-1 mb-1 w-full bg-[#0b0d12] border border-[#2C2D35] px-3 py-2.5 text-lg font-mono flex items-center justify-between">
              <span>{unit || "—"}</span>
              <span className="text-[10px] text-[#4ade80] flex items-center gap-1"><ShieldCheck size={12} /> ASSIGNED</span>
            </div>
            <div className="font-mono text-[10px] text-[#9CA3AF] mb-4">Your unit is assigned to your account and cannot be changed here.</div>
            <label className="font-mono text-[11px] text-[#6b8bd6] uppercase flex items-center gap-1"><Navigation size={11} /> Vehicle Assignment</label>
            <input data-testid="signon-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)}
              placeholder="e.g. CAR-47, TRUCK-12 (optional)" className="mt-1 w-full bg-[#0b0d12] border border-[#2C2D35] px-3 py-2.5 text-sm font-mono outline-none focus:border-[#3B82F6]" />
            <div className="font-mono text-[10px] text-[#9CA3AF] mt-1">Leave blank to match unit number</div>
            <div className="font-mono text-[11px] text-[#9CA3AF] mt-4">
              Sign-on time: <span className="text-[#F3F4F6]">{now.toLocaleTimeString()}</span><br />
              Date: <span className="text-[#F3F4F6]">{now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
            </div>
            <button data-testid="signon-btn" onClick={signOn}
              className="mt-5 w-full flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563eb] text-white font-head font-semibold py-3 text-sm transition-colors">
              <ShieldCheck size={16} /> SIGN ON & BEGIN SHIFT
            </button>
          </div>
        </div>
      )}

      {step === "location" && (
        <div className="w-full max-w-md" data-testid="location-verify">
          <div className="border border-[#2C2D35] bg-[#111621] p-6">
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-full bg-[#1b2a44] flex items-center justify-center mx-auto mb-3"><Navigation size={24} className="text-[#3B82F6]" /></div>
              <div className="font-head font-bold text-xl tracking-tight">LOCATION VERIFICATION</div>
              <div className="font-mono text-xs text-[#6b8bd6] mt-1">REQUIRED AT LOGIN — OFFICER MDT</div>
            </div>
            <div className="border border-[#2C2D35] bg-[#0b0d12] text-center py-3 mb-4">
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Logged in as</div>
              <div className="font-head font-bold">{user?.name}</div>
            </div>
            <div className="border border-[#5c4a16] bg-[#241d0b] text-[#e3c766] text-[11px] font-mono p-3 mb-4 flex gap-2">
              <ShieldCheck size={26} className="shrink-0" />
              <span>Department policy requires GPS location tracking during all active duty shifts. Your location will be continuously shared with dispatch and supervisors. All tracking data is logged per agency retention policy.</span>
            </div>
            <button data-testid="location-gps-btn" onClick={enableGps} disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563eb] text-white font-head font-semibold py-3 text-sm mb-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />} ENABLE GPS & CONFIRM LOCATION
            </button>
            <button data-testid="location-manual-btn" onClick={() => finish("manual")}
              className="w-full border border-[#2C2D35] bg-[#0b0d12] hover:bg-[#141518] font-head py-3 text-sm transition-colors">
              Enter Location Manually
            </button>
            <button data-testid="location-skip-btn" onClick={() => finish("skipped")}
              className="w-full text-center font-mono text-[11px] text-[#6b7280] hover:text-[#9CA3AF] mt-3">
              Skip (not recommended — will be logged)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
